import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronRight, Database, Pencil, Trash2, Upload, Download, X, Plus } from 'lucide-react'
import {
  getDataset, updateDataset, deleteDataset,
  getDocumentsByDataset, uploadDocument, updateDocument, deleteDocument, downloadDocument,
  getDatasetPermissions, addDatasetPermission, removeDatasetPermission,
} from '../api/storage'
import { Modal } from '../components/Modal'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'
import { Select, SelectItem } from '../components/ui/select'
import { useDateFmt } from '../hooks/useDateFmt'
import type { Dataset, StorageDocument, DatasetPermission } from '../types'

type Tab = 'overview' | 'documents' | 'permissions'

function fmtSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1_048_576) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1_048_576).toFixed(1)} MB`
}

const PERMISSION_TYPES = ['owner', 'writer', 'member', 'reader']

// ── Overview tab ──────────────────────────────────────────────────────────────

function OverviewTab({ dataset }: { dataset: Dataset }) {
  const fmt = useDateFmt()
  if (!dataset) return null
  return (
    <div className="flex gap-6">
      <div className="flex-1 min-w-0">
        <div className="mb-5">
          <div className="text-theme-xs font-semibold text-ink-dim uppercase tracking-widest mb-1.5">Description</div>
          {dataset.description ? (
            <p className="text-sm text-ink-dim leading-relaxed">{dataset.description}</p>
          ) : (
            <p className="text-sm text-ink-dim italic">No description</p>
          )}
        </div>

        <div>
          <div className="text-theme-xs font-semibold text-ink-dim uppercase tracking-widest mb-3">Retention policy</div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Policy</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="font-medium">Archive after</td>
                  <td className="text-ink-dim">{dataset.archivePeriodDays} days</td>
                </tr>
                <tr>
                  <td className="font-medium">Purge after</td>
                  <td className="text-ink-dim">{dataset.purgePeriodDays} days</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="w-60 shrink-0 space-y-5">
        <div className="border border-paper-border rounded-lg p-4 text-sm space-y-3">
          <div className="text-theme-xs font-semibold text-ink-dim uppercase tracking-widest">About this dataset</div>
          <div className="space-y-2.5">
            <div>
              <div className="text-theme-xs text-ink-dim mb-0.5">ID</div>
              <div className="font-mono text-theme-xs text-ink-dim break-all">{dataset.id}</div>
            </div>
            <div>
              <div className="text-theme-xs text-ink-dim mb-0.5">Owner service</div>
              <div className="text-ink font-medium">{dataset.ownerServiceId}</div>
            </div>
            <div>
              <div className="text-theme-xs text-ink-dim mb-0.5">Created</div>
              <div className="text-ink-dim">{fmt(dataset.createdAt)}</div>
            </div>
            <div>
              <div className="text-theme-xs text-ink-dim mb-0.5">Updated</div>
              <div className="text-ink-dim">{fmt(dataset.updatedAt)}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Documents tab ─────────────────────────────────────────────────────────────

function DocumentsTab({ datasetId }: { datasetId: string }) {
  const qc = useQueryClient()
  const fmt = useDateFmt()

  const { data: docs = [], isLoading } = useQuery({
    queryKey: ['documents', datasetId],
    queryFn: () => getDocumentsByDataset(datasetId).then(r => r.data),
  })

  const [showUpload, setShowUpload] = useState(false)
  const [uploadForm, setUploadForm] = useState({ title: '', description: '', file: null as File | null })
  const upload = useMutation({
    mutationFn: () => uploadDocument({ datasetId, title: uploadForm.title, description: uploadForm.description, file: uploadForm.file! }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['documents', datasetId] }); setShowUpload(false); setUploadForm({ title: '', description: '', file: null }) },
  })

  const [editDoc, setEditDoc] = useState<StorageDocument | null>(null)
  const [editDocForm, setEditDocForm] = useState({ title: '', description: '' })
  const openEditDoc = (d: StorageDocument) => { setEditDoc(d); setEditDocForm({ title: d.title, description: d.description ?? '' }) }
  const updateDoc = useMutation({
    mutationFn: () => updateDocument(editDoc!.id, editDocForm),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['documents', datasetId] }); setEditDoc(null) },
  })

  const [deleteDoc, setDeleteDoc] = useState<StorageDocument | null>(null)
  const delDoc = useMutation({
    mutationFn: (id: string) => deleteDocument(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['documents', datasetId] }); setDeleteDoc(null) },
  })

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="text-theme-xs font-semibold text-ink-dim uppercase tracking-widest">
          {isLoading ? 'Documents' : `Documents (${docs.length})`}
        </span>
        <Button icon={Upload} onClick={() => setShowUpload(true)}>Upload</Button>
      </div>

      {isLoading ? (
        <div className="py-8 text-center text-sm text-ink-dim">Loading…</div>
      ) : docs.length === 0 ? (
        <div className="border border-dashed border-paper-border rounded-lg py-12 text-center text-sm text-ink-dim">
          No documents in this dataset yet.
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Type</th>
                <th>Size</th>
                <th>Added</th>
                <th className="w-20" />
              </tr>
            </thead>
            <tbody>
              {(docs as StorageDocument[]).map(d => (
                <tr key={d.id}>
                  <td>
                    <p className="font-medium text-ink">{d.title}</p>
                    {d.description && <p className="text-xs text-ink-dim mt-0.5">{d.description}</p>}
                  </td>
                  <td className="text-ink-dim font-mono text-theme-xs">{d.mimeType}</td>
                  <td className="text-ink-dim">{fmtSize(d.fileSize)}</td>
                  <td className="text-ink-dim">{fmt(d.createdAt)}</td>
                  <td>
                    <div className="flex items-center gap-2 justify-end">
                      <button onClick={() => downloadDocument(d.id, d.title)}
                        className="text-ink-dim hover:text-ink transition-colors" title="Download">
                        <Download size={13} />
                      </button>
                      <button onClick={() => openEditDoc(d)}
                        className="text-ink-dim hover:text-ink transition-colors" title="Edit">
                        <Pencil size={13} />
                      </button>
                      <button onClick={() => setDeleteDoc(d)}
                        className="text-ink-dim hover:text-red-500 transition-colors" title="Delete">
                        <X size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showUpload && (
        <Modal title="Upload document" onClose={() => setShowUpload(false)} size="sm">
          <div className="space-y-4">
            <Field label="Title *">
              <input value={uploadForm.title} onChange={e => setUploadForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Document title" className="field" autoFocus />
            </Field>
            <Field label="Description">
              <input value={uploadForm.description} onChange={e => setUploadForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Optional" className="field" />
            </Field>
            <Field label="File *">
              <input type="file" onChange={e => setUploadForm(f => ({ ...f, file: e.target.files?.[0] ?? null }))}
                className="w-full text-sm text-ink-dim file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-paper-sunken file:text-ink file:text-xs" />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setShowUpload(false)}>Cancel</Button>
              <Button onClick={() => upload.mutate()} disabled={!uploadForm.title || !uploadForm.file} loading={upload.isPending}>
                Upload
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {editDoc && (
        <Modal title="Edit document" onClose={() => setEditDoc(null)} size="sm">
          <div className="space-y-4">
            <Field label="Title *">
              <input value={editDocForm.title} onChange={e => setEditDocForm(f => ({ ...f, title: e.target.value }))}
                className="field" autoFocus />
            </Field>
            <Field label="Description">
              <input value={editDocForm.description} onChange={e => setEditDocForm(f => ({ ...f, description: e.target.value }))}
                className="field" />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setEditDoc(null)}>Cancel</Button>
              <Button onClick={() => updateDoc.mutate()} disabled={!editDocForm.title} loading={updateDoc.isPending}>
                Save
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {deleteDoc && (
        <ConfirmDialog
          title="Delete document"
          message={`Delete "${deleteDoc.title}"? This cannot be undone.`}
          onCancel={() => setDeleteDoc(null)}
          onConfirm={() => delDoc.mutate(deleteDoc.id)}
          loading={delDoc.isPending}
        />
      )}
    </div>
  )
}

// ── Permissions tab ───────────────────────────────────────────────────────────

function PermissionsTab({ datasetId }: { datasetId: string }) {
  const qc = useQueryClient()
  const fmt = useDateFmt()

  const { data: permissions = [], isLoading } = useQuery({
    queryKey: ['dataset-permissions', datasetId],
    queryFn: () => getDatasetPermissions(datasetId).then(r => r.data),
  })

  const [form, setForm] = useState({ serviceId: '', permissionType: 'reader' })
  const addPerm = useMutation({
    mutationFn: () => addDatasetPermission(datasetId, form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dataset-permissions', datasetId] }); setForm(f => ({ ...f, serviceId: '' })) },
  })

  const [deleteTarget, setDeleteTarget] = useState<DatasetPermission | null>(null)
  const delPerm = useMutation({
    mutationFn: (p: DatasetPermission) => removeDatasetPermission(datasetId, p.serviceId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dataset-permissions', datasetId] }); setDeleteTarget(null) },
  })

  const PERM_COLORS: Record<string, string> = {
    owner:  'bg-amber-50 text-amber-600 border-amber-100',
    writer: 'bg-violet-50 text-violet-600 border-violet-100',
    member: 'bg-blue-50 text-blue-600 border-blue-100',
    reader: 'bg-emerald-50 text-emerald-600 border-emerald-100',
  }

  return (
    <div>
      <div className="text-theme-xs font-semibold text-ink-dim uppercase tracking-widest mb-4">Permissions</div>

      {isLoading ? (
        <div className="py-8 text-center text-sm text-ink-dim">Loading…</div>
      ) : permissions.length === 0 ? (
        <div className="border border-dashed border-paper-border rounded-lg py-8 text-center text-sm text-ink-dim mb-4">
          No permissions configured.
        </div>
      ) : (
        <div className="space-y-1.5 mb-5">
          {(permissions as DatasetPermission[]).map(p => (
            <div key={p.serviceId} className="flex items-center justify-between px-3 py-2.5 rounded-lg border border-paper-border hover:bg-paper-sunken">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className={`text-[10px] font-medium px-1.5 py-px rounded border shrink-0 ${PERM_COLORS[p.permissionType] ?? 'bg-paper-sunken text-ink-dim border-paper-border'}`}>
                  {p.permissionType}
                </span>
                <span className="text-sm font-medium text-ink truncate">{p.serviceId}</span>
                <span className="text-xs text-ink-dim shrink-0">since {fmt(p.assignedAt)}</span>
              </div>
              <button onClick={() => setDeleteTarget(p)}
                className="text-ink-dim hover:text-red-500 transition-colors ml-4 shrink-0">
                <X size={13} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="border border-paper-border rounded-lg p-4 space-y-3">
        <div className="text-theme-xs font-semibold text-ink-dim uppercase tracking-widest">Add permission</div>
        <div className="flex gap-2">
          <input
            value={form.serviceId}
            onChange={e => setForm(f => ({ ...f, serviceId: e.target.value }))}
            placeholder="service-id"
            className="field flex-1"
          />
          <Select
            value={form.permissionType}
            onValueChange={v => setForm(f => ({ ...f, permissionType: v }))}
            className="w-32"
          >
            {PERMISSION_TYPES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </Select>
          <Button
            icon={Plus}
            onClick={() => addPerm.mutate()}
            disabled={!form.serviceId}
            loading={addPerm.isPending}
            className="shrink-0"
          >
            Add
          </Button>
        </div>
      </div>

      {deleteTarget && (
        <ConfirmDialog
          title="Remove permission"
          message={`Remove ${deleteTarget.permissionType} access for "${deleteTarget.serviceId}"?`}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => delPerm.mutate(deleteTarget)}
          loading={delPerm.isPending}
        />
      )}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function DatasetDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [tab, setTab] = useState<Tab>('overview')
  const [editing, setEditing] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const [form, setForm] = useState({ title: '', description: '', archivePeriodDays: '', purgePeriodDays: '' })

  const { data: dataset, isLoading } = useQuery({
    queryKey: ['dataset', id],
    queryFn: () => getDataset(id!).then(r => r.data),
    enabled: !!id,
  })

  const editMut = useMutation({
    mutationFn: () => updateDataset(id!, {
      title: form.title,
      description: form.description || undefined,
      archivePeriodDays: parseInt(form.archivePeriodDays),
      purgePeriodDays: parseInt(form.purgePeriodDays),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dataset', id] })
      qc.invalidateQueries({ queryKey: ['datasets'] })
      setEditing(false)
    },
  })

  const delMut = useMutation({
    mutationFn: () => deleteDataset(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['datasets'] })
      navigate('/datasets')
    },
  })

  function openEdit() {
    if (!dataset) return
    setForm({
      title: dataset.title,
      description: dataset.description ?? '',
      archivePeriodDays: String(dataset.archivePeriodDays),
      purgePeriodDays: String(dataset.purgePeriodDays),
    })
    setEditing(true)
  }

  if (isLoading) return <div className="py-16 text-center text-sm text-ink-dim">Loading…</div>
  if (!dataset)  return <div className="py-16 text-center text-sm text-red-500">Dataset not found</div>

  const TAB_LABELS: Record<Tab, string> = { overview: 'Overview', documents: 'Documents', permissions: 'Permissions' }

  return (
    <div>
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 mb-3 text-sm">
        <button onClick={() => navigate('/datasets')}
          className="text-ink-dim hover:text-ink transition-colors">
          Datasets
        </button>
        <ChevronRight size={13} className="text-ink-dim" />
        <span className="text-ink font-medium">{dataset.title}</span>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Database size={16} className="text-ink-dim shrink-0" />
          <h1 className="text-lg font-semibold text-ink">{dataset.title}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" icon={Pencil} onClick={openEdit}>Edit</Button>
          <button onClick={() => setShowDelete(true)} className="text-ink-dim hover:text-red-500 transition-colors p-1.5">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b border-paper-border mb-5">
        <nav className="flex -mb-px">
          {(['overview', 'documents', 'permissions'] as Tab[]).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === t
                  ? 'border-accent text-accent'
                  : 'border-transparent text-gray-500 hover:text-gray-700-dim hover:text-ink'
              }`}
            >
              {TAB_LABELS[t]}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      {tab === 'overview' && <OverviewTab dataset={dataset} />}
      {tab === 'documents' && <DocumentsTab datasetId={id!} />}
      {tab === 'permissions' && <PermissionsTab datasetId={id!} />}

      {/* Edit modal */}
      {editing && (
        <Modal title="Edit dataset" onClose={() => setEditing(false)} size="sm">
          <div className="space-y-4">
            <Field label="Title *">
              <input
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Dataset title"
                className="field"
                autoFocus
              />
            </Field>
            <Field label="Description">
              <input
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Optional"
                className="field"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Archive after (days) *">
                <input
                  type="number"
                  value={form.archivePeriodDays}
                  onChange={e => setForm(f => ({ ...f, archivePeriodDays: e.target.value }))}
                  min="1"
                  className="field"
                />
              </Field>
              <Field label="Purge after (days) *">
                <input
                  type="number"
                  value={form.purgePeriodDays}
                  onChange={e => setForm(f => ({ ...f, purgePeriodDays: e.target.value }))}
                  min="2"
                  className="field"
                />
              </Field>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setEditing(false)}>Cancel</Button>
              <Button
                onClick={() => editMut.mutate()}
                disabled={!form.title}
                loading={editMut.isPending}
              >
                Save
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete confirm */}
      {showDelete && (
        <ConfirmDialog
          title="Delete dataset"
          message={`Delete dataset "${dataset.title}" and all its documents? This cannot be undone.`}
          onCancel={() => setShowDelete(false)}
          onConfirm={() => delMut.mutate()}
          loading={delMut.isPending}
        />
      )}
    </div>
  )
}
